import { type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { dismissLimit } from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';

import type { DraggableEvent } from 'react-draggable';

const ELEMENT_WIDTH = 718;
const ELEMENT_HEIGHT = 50;
const CATALOG_POSITION_Y = 130;
const DEFAULT_POSITION_Y = 180;
type SelectorPosition = { x: number; y: number };

const useSymbolSelector = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const symbols = useAppSelector(state => state.symbolSelector.symbols);
  const isDashboardAppSelector = useAppSelector(
    state => state.symbolSelector.isDashboardAppSelector
  );
  const isInCatalog = useMemo(() => {
    return (
      location.pathname.toLowerCase().includes('/catalog') && !searchParams.get('indexInformation')
    );
  }, [location.pathname, searchParams]);

  const calculateDefaultPosition = useCallback(() => {
    const x = (window.innerWidth - ELEMENT_WIDTH) / 2;
    const y = isInCatalog ? window.innerHeight - CATALOG_POSITION_Y : DEFAULT_POSITION_Y;
    return { x, y };
  }, [isInCatalog]);

  const selectionKey = useMemo(
    () => symbols.map(symbol => JSON.stringify(symbol)).join('|'),
    [symbols]
  );

  const defaultPosition = useMemo(() => {
    if (symbols.length === 0) return;
    return calculateDefaultPosition();
  }, [calculateDefaultPosition, symbols.length]);

  const [positionState, setPositionState] = useState<{
    selectionKey: string;
    position: SelectorPosition | undefined;
  } | null>(null);

  const position = useMemo(() => {
    if (positionState?.selectionKey === selectionKey) return positionState.position;
    if (symbols.length === 0) return;
    return defaultPosition;
  }, [defaultPosition, positionState, selectionKey, symbols.length]);

  const setPosition = useCallback(
    (update: SetStateAction<SelectorPosition | undefined>) => {
      setPositionState(prev => {
        const currentPosition =
          prev?.selectionKey === selectionKey ? prev.position : defaultPosition;
        const nextPosition = typeof update === 'function' ? update(currentPosition) : update;
        return { selectionKey, position: nextPosition };
      });
    },
    [defaultPosition, selectionKey]
  );

  const constrainToViewport = useCallback((pos: SelectorPosition) => {
    const x = Math.max(0, Math.min(pos.x, window.innerWidth - ELEMENT_WIDTH));
    const y = Math.max(0, Math.min(pos.y, window.innerHeight - ELEMENT_HEIGHT));
    return { x, y };
  }, []);

  const handleResize = useCallback(() => {
    if (position) {
      setPosition(constrainToViewport(position));
    }
  }, [constrainToViewport, position, setPosition]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const handleDrag = useCallback(
    (_: DraggableEvent, data: SelectorPosition) => {
      setPosition({ x: data.x, y: data.y });
    },
    [setPosition]
  );

  const handleClose = useCallback(() => {
    dispatch(dismissLimit());
  }, [dispatch]);

  return {
    position,
    setPosition,
    handleDrag,
    handleClose,
    navigate,
    isInCatalog,
    isDashboardAppSelector
  };
};

export default useSymbolSelector;
